import React, { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormikProps } from 'formik';
import { object, string } from 'yup';
import isEmpty from 'lodash/isEmpty';

// hooks
import useOnMount from 'hooks/useOnMount';
import useSharedState from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedState';
import useSharedBoolean from 'pages/MerchantPages/Assets/containers/EditAsset/hooks/useSharedBoolean';
import { useUncontrolledSwitch } from 'components/Switcher';

// components
import Loader from 'components/Loader';
import FormikWrapper from 'components/FormikWrapper';

// actions
import { getIvsChannelDetails, IvsChannel, updateIvsChannelDetails } from 'ducks/merchantDucks/items/actions';

// selectors
import { getItemDetails, getIvsChannelsData } from 'ducks/merchantDucks/items/selectors';

// own types
import { IvsContentObject } from '../IvsContent';

// own components
import ChannelOptions from './ChannelOptions';

const validationSchema = object().shape({
  name: string().required('Channel name is required'),
});

interface Props {
  closePanel: () => void;
  onSubmit: (channelContent: IvsContentObject) => void;
}

const EditChannel: FC<Props> = ({ closePanel, onSubmit }) => {
  const dispatch = useDispatch();

  const { resetToDefault } = useUncontrolledSwitch();

  const { isFetching: isItemDetailsFetching } = useSelector(getItemDetails);

  const {
    status: { isFetching: isIvsChannelsFetching },
  } = useSelector(getIvsChannelsData);

  const [currentChannelArn] = useSharedState('currentChannelArn', '');
  const [, setSaveButtonDisabled] = useSharedBoolean('isSaveButtonDisabled', false);

  const [channelDetails, setChannelDetails] = useState<IvsChannel | null>(null);

  useOnMount(() => {
    (async () => {
      const data = await Promise.resolve(dispatch(getIvsChannelDetails(currentChannelArn)));

      if (data) {
        setChannelDetails(data as IvsChannel);
      }
    })();
  });

  if (!channelDetails || isIvsChannelsFetching || isItemDetailsFetching) {
    return <Loader position="absolute" />;
  }

  const handleSubmit = async (values: IvsChannel) => {
    await Promise.resolve(dispatch(updateIvsChannelDetails(values)));

    const { arn, stream_url } = values;

    onSubmit({ arn, stream_url });
  };

  const handleStatusChange = ({ isSubmitting, dirty, errors }: FormikProps<IvsChannel>) => {
    setSaveButtonDisabled(isSubmitting || !dirty || !isEmpty(errors));
  };

  return (
    <FormikWrapper<IvsChannel>
      initialValues={channelDetails}
      onSubmit={handleSubmit}
      closePanel={closePanel}
      validationSchema={validationSchema}
      onStatusChange={handleStatusChange}
      validateOnMount
    >
      <ChannelOptions title="Edit IVS channel" onCancel={resetToDefault} />
    </FormikWrapper>
  );
};

export default EditChannel;
